import { onBeforeUnmount, onMounted, ref } from 'vue'
import { onLCP, onTTFB } from 'web-vitals'

type LayoutShiftAttribution = {
    node?: Node
}

type LayoutShiftEntry = PerformanceEntry & {
    value: number
    hadRecentInput: boolean
    sources?: LayoutShiftAttribution[]
}

type SessionWindow = {
    startTs: number
    lastTs: number
    value: number
    sources: string[]
}

type UseBrowserRumTrackingOptions = {
    clsThreshold: number
    onHighCls: (payload: { cls: number; viewport: string; sources: string }) => void
    onTTFB: (ttfb: number) => void
    onLCP: (lcp: number) => void
}

const CLS_SESSION_WINDOW_GAP_MS = 1000
const CLS_SESSION_WINDOW_MAX_MS = 5000

function getViewportString(): string {
    const width = Math.round(window.visualViewport?.width ?? window.innerWidth)
    const height = Math.round(window.visualViewport?.height ?? window.innerHeight)
    const dpr = window.devicePixelRatio || 1
    const dprStr = Number.isFinite(dpr) ? dpr.toFixed(2).replace(/\.00$/, '') : '1'
    return `${width}x${height}@${dprStr}`
}

function getNodeSelector(node: Node | undefined): string | null {
    if (!node || node.nodeType !== Node.ELEMENT_NODE) {
        return null
    }

    const el = node as Element
    const id = el.getAttribute('id')
    if (id) {
        return `#${id}`
    }

    const tag = el.tagName.toLowerCase()
    const classAttr = el.getAttribute('class') || ''
    const classes = classAttr
        .split(/\s+/)
        .map((c) => c.trim())
        .filter(Boolean)
        .slice(0, 2)

    if (classes.length > 0) {
        return `${tag}.${classes.join('.')}`
    }

    return tag
}

function compactSources(sources: string[]): string {
    const unique: string[] = []
    const seen = new Set<string>()

    for (const source of sources) {
        if (!source) {
            continue
        }
        if (seen.has(source)) {
            continue
        }
        seen.add(source)
        unique.push(source)
        if (unique.length >= 2) {
            break
        }
    }

    const result = unique.join(',')
    return result.length > 120 ? result.slice(0, 120) : result
}

function extractSourcesFromEntry(entry: LayoutShiftEntry): string[] {
    const result: string[] = []
    const rawSources = entry.sources || []

    for (const source of rawSources) {
        const selector = getNodeSelector(source.node)
        if (selector) {
            result.push(selector)
        }
    }

    return result
}

export function useBrowserRumTracking(options: UseBrowserRumTrackingOptions): void {
    const observerRef = ref<PerformanceObserver | null>(null)
    const bestSessionRef = ref<SessionWindow | null>(null)
    const currentSessionRef = ref<SessionWindow | null>(null)
    const hasLoggedRef = ref(false)

    const maybeLog = (session: SessionWindow | null) => {
        if (hasLoggedRef.value) {
            return
        }

        if (!session) {
            return
        }

        if (session.value < options.clsThreshold) {
            return
        }

        hasLoggedRef.value = true
        options.onHighCls({
            cls: session.value,
            viewport: getViewportString(),
            sources: compactSources(session.sources),
        })
    }

    const onEntries: PerformanceObserverCallback = (list) => {
        for (const e of list.getEntries()) {
            const entry = e as LayoutShiftEntry

            if (entry.entryType !== 'layout-shift') {
                continue
            }

            if (entry.hadRecentInput) {
                continue
            }

            const ts = entry.startTime
            const value = entry.value || 0
            if (!Number.isFinite(value) || value <= 0) {
                continue
            }

            const currentSession = currentSessionRef.value
            if (!currentSession) {
                currentSessionRef.value = {
                    startTs: ts,
                    lastTs: ts,
                    value,
                    sources: extractSourcesFromEntry(entry),
                }
            } else {
                const isGapExceeded = ts - currentSession.lastTs > CLS_SESSION_WINDOW_GAP_MS
                const isMaxWindowExceeded = ts - currentSession.startTs > CLS_SESSION_WINDOW_MAX_MS

                if (isGapExceeded || isMaxWindowExceeded) {
                    currentSessionRef.value = {
                        startTs: ts,
                        lastTs: ts,
                        value,
                        sources: extractSourcesFromEntry(entry),
                    }
                } else {
                    currentSessionRef.value = {
                        ...currentSession,
                        lastTs: ts,
                        value: currentSession.value + value,
                        sources: [...currentSession.sources, ...extractSourcesFromEntry(entry)],
                    }
                }
            }

            const nextSession = currentSessionRef.value
            const bestSession = bestSessionRef.value
            if (nextSession && (!bestSession || nextSession.value > bestSession.value)) {
                bestSessionRef.value = {
                    ...nextSession,
                    sources: [...nextSession.sources],
                }
            }

            // Log ASAP if the running session crossed the threshold.
            // This avoids waiting for session window end or page lifecycle events.
            maybeLog(currentSessionRef.value)
        }
    }

    const stop = () => {
        observerRef.value?.disconnect()
        observerRef.value = null
    }

    const start = () => {
        if (typeof PerformanceObserver !== 'undefined') {
            try {
                observerRef.value = new PerformanceObserver(onEntries)
                observerRef.value.observe({ type: 'layout-shift', buffered: true } as PerformanceObserverInit)
            } catch {
                observerRef.value = null
            }
        }

        onTTFB((metric) => {
            options.onTTFB(metric.value)
        })

        onLCP((metric) => {
            options.onLCP(metric.value)
        })
    }

    onMounted(start)
    onBeforeUnmount(stop)
}
